interface CheckboxProps {
  /** Native control for forms that require standard checkbox semantics. */
  native?: boolean
  checked: boolean
  onChange: (checked: boolean) => void
  label?: React.ReactNode
  hint?: string
  disabled?: boolean
  /** Opt-in larger box; existing checkboxes keep their 18 px default. */
  size?: 'sm' | 'lg'
  /** Accessible name for label-less usage (e.g. table row selection). */
  ariaLabel?: string
  /** Extra classes on the outer label (e.g. `w-full`). */
  className?: string
  /** Replaces the hint's default size class `text-[10px]`. */
  hintClassName?: string
  ariaDescribedBy?: string
}

export function Checkbox({ native = false, checked, onChange, label, hint, disabled, ariaLabel, ariaDescribedBy, className, hintClassName, size = 'sm' }: CheckboxProps) {
  if (native) return <label className="inline-flex min-h-9 items-center gap-2 text-sm">
    <input type="checkbox" checked={checked} disabled={disabled} aria-label={ariaLabel} aria-describedby={ariaDescribedBy} onChange={e => onChange(e.target.checked)} className="h-[18px] w-[18px] accent-primary" />
    <span>{label}{hint && <span className="block text-xs text-on-surface-variant">{hint}</span>}</span>
  </label>
  return (
    <label className={`inline-flex items-start gap-2.5 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={ariaLabel} aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative flex-shrink-0 ${size === 'lg' ? 'w-6 h-6' : 'w-[18px] h-[18px]'} mt-px rounded-[5px] border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30`}
        style={{
          borderColor: checked ? '#0d7c76' : '#cbd5e1',
          background: checked ? '#0d7c76' : '#fff',
          transition: 'background 150ms ease, border-color 150ms ease',
        }}
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="absolute inset-0 m-auto" style={{ width: size === 'lg' ? 16 : 11, height: size === 'lg' ? 16 : 11 }}>
            <path d="M2.5 6l2.5 2.5 4.5-5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {(label || hint) && (
        <div className="min-w-0">
          {label && <div className="text-[12px] font-semibold text-on-surface">{label}</div>}
          {hint && <div className={`${hintClassName ?? 'text-[10px]'} text-on-surface-variant`}>{hint}</div>}
        </div>
      )}
    </label>
  )
}
